/**
 * PeerEndorsements — what colleagues have said about one of the nurse's skills.
 *
 * READ-ONLY to the owner, and that is the change. The old surface let her type
 * a label and "accept" a colleague's feedback, which wrote a fresh attestation
 * in her own words on the back of what someone else said. The learning design removed
 * that: the endorsement is the colleague's record and the declaration is hers,
 * two facts with two writers. She controls whether the SKILL these sit behind
 * is published — the publish and unpublish buttons on the skill row — and never
 * what a colleague wrote.
 *
 * The 1-to-5 star rating is gone with it. An endorsement names a skill and
 * carries an optional comment; there is no score on the wire and inventing one
 * on screen would be worse than showing none.
 *
 * Privacy (nDSG): the endorser is identified by `performer`, a `User/<id>`
 * reference. When it carries no display string we do not have a name and must
 * not invent one, so the row reads the i18n "colleague" key.
 *
 * The records are read on demand — this component mounts only when the nurse
 * opens a row — rather than eagerly across the whole fold, because there is no
 * route that returns every endorsement at once and a page-load fan-out over
 * every skill would be one request per row.
 */

import { useTranslation } from 'react-i18next'
import { instantDay } from '@frontend/utils/dates'
import { Skeleton } from '@frontend/components/ui/skeleton'
import type { Endorsement } from '@frontend/api/endpoints/skills'
import { useSkillEndorsements } from '@frontend/hooks/useSkills'

function EndorsementRow({ endorsement }: { endorsement: Endorsement }) {
  const { t } = useTranslation()
  const who = endorsement.performer.display || t('skills.endorsements.colleague')

  return (
    <li className="space-y-2 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[15px] font-semibold text-[var(--color-surface-ink)]">{who}</span>
        <span className="text-[12px] text-[var(--color-surface-mute)]">
          {instantDay(endorsement.occurred)}
        </span>
      </div>
      {/* A null comment is an endorsement without words, which is a real answer.
          It gets the "endorsed without a comment" line rather than a blank row,
          so the nurse can tell it apart from a comment that failed to render. */}
      {endorsement.comment ? (
        <p className="text-[17px] text-[var(--color-surface-ink)]">{endorsement.comment}</p>
      ) : (
        <p className="text-[15px] italic text-[var(--color-surface-mute)]">
          {t('skills.endorsements.noComment')}
        </p>
      )}
    </li>
  )
}

export function PeerEndorsements({ skillKey }: { skillKey: string }) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useSkillEndorsements(skillKey, true)

  if (isLoading) {
    return <Skeleton className="h-16 w-full rounded-[18px]" />
  }

  // A failed read is never rendered as "no colleague has endorsed this" — that
  // is a claim about what colleagues did, made from a request that never
  // answered.
  if (isError || !data) {
    return (
      <p className="rounded-[18px] border border-dashed border-[var(--color-tone-warn)] px-5 py-4 text-[15px] text-[var(--color-tone-warn)]">
        {t('skills.endorsements.loadFailed')}
      </p>
    )
  }

  if (data.items.length === 0) {
    return (
      <p className="rounded-[18px] border border-dashed border-[var(--color-surface-border)] px-5 py-4 text-[15px] text-[var(--color-surface-mute)]">
        {t('skills.endorsements.empty')}
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {data.items.map((endorsement) => (
        <EndorsementRow key={endorsement.id} endorsement={endorsement} />
      ))}
    </ul>
  )
}

export default PeerEndorsements
